import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ADMISSIONS_STEPS, PROGRAMS } from "../data/content";
import {
  ACADEMIC_YEARS,
  COUNTRY_CODES,
  GENDERS,
  PREFERRED_STARTS,
  SOURCES,
} from "../data/admissionsData";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import axios from "axios";
import { toast } from "sonner";
import {
  CheckCircle2,
  Send,
  GraduationCap,
  Baby,
  Users,
  Sparkles,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const schema = z.object({
  // Program
  academic_year: z.string().min(1, "Please choose an academic year"),
  program: z.string().min(1, "Please choose a program"),
  preferred_start: z.string().optional(),

  // Student
  student_name: z.string().min(2, "Please enter the child's full name"),
  child_dob: z.string().min(4, "Please enter date of birth"),
  gender: z.string().min(1, "Please select"),
  nationality: z.string().optional(),
  mother_tongue: z.string().optional(),
  medical_notes: z.string().optional(),

  // Parents
  father_name: z.string().min(2, "Please enter father's name"),
  father_occupation: z.string().optional(),
  mother_name: z.string().min(2, "Please enter mother's name"),
  mother_occupation: z.string().optional(),
  email: z.string().email("Enter a valid email"),
  country_code: z.string().min(1),
  mobile: z.string().min(5, "Enter a valid mobile number"),
  alt_phone: z.string().optional(),
  address: z.string().optional(),

  // Source
  source: z.string().min(1, "Please tell us how you heard about Naavi"),
  referred_by: z.string().optional(),
  message: z.string().optional(),
});

const SECTIONS = [
  { id: "program", label: "Program", icon: GraduationCap },
  { id: "student", label: "Child", icon: Baby },
  { id: "parents", label: "Parents", icon: Users },
  { id: "source", label: "Source", icon: Sparkles },
];

export default function Admissions() {
  const [submitted, setSubmitted] = useState(false);
  const [activeSection, setActiveSection] = useState("program");

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      academic_year: "",
      program: "",
      preferred_start: "",
      student_name: "",
      child_dob: "",
      gender: "",
      nationality: "",
      mother_tongue: "",
      medical_notes: "",
      father_name: "",
      father_occupation: "",
      mother_name: "",
      mother_occupation: "",
      email: "",
      country_code: "+91",
      mobile: "",
      alt_phone: "",
      address: "",
      source: "",
      referred_by: "",
      message: "",
    },
  });

  const sourceValue = watch("source");

  const onSubmit = async (values) => {
    try {
      await axios.post(`${API}/admissions`, values);
      setSubmitted(true);
      reset();
      setActiveSection("program");
      toast.success("Your enquiry has been received. We'll be in touch within 24 hours.");
      // smooth-scroll back up to the success banner
      setTimeout(() => {
        document.getElementById("admissions-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    } catch (e) {
      const msg = e?.response?.data?.detail
        ? "Please check the highlighted fields and try again."
        : "Something went wrong. Please try again in a moment.";
      toast.error(msg);
    }
  };

  const onInvalid = (errs) => {
    // Jump to the first section containing an error
    const fieldToSection = {
      academic_year: "program", program: "program", preferred_start: "program",
      student_name: "student", child_dob: "student", gender: "student",
      nationality: "student", mother_tongue: "student", medical_notes: "student",
      father_name: "parents", father_occupation: "parents",
      mother_name: "parents", mother_occupation: "parents",
      email: "parents", country_code: "parents", mobile: "parents",
      alt_phone: "parents", address: "parents",
      source: "source", referred_by: "source", message: "source",
    };
    const firstKey = Object.keys(errs)[0];
    if (firstKey) {
      setActiveSection(fieldToSection[firstKey] || "program");
      toast.error("Please complete the highlighted fields.");
    }
  };

  return (
    <section
      id="admissions"
      className="relative py-14 md:py-28 bg-[#FAFAF5]"
      data-testid="admissions-section"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-16">
          <div className="lg:col-span-6">
            <span className="text-xs font-bold tracking-[0.22em] uppercase text-[#648DE5] mb-5 block">
              Admissions Enquiry · 2026 — 27
            </span>
            <h2 className="font-display text-[#0F1B47] text-[32px] md:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-semibold">
              Begin the
              <br />
              <span className="italic font-normal">Naavi journey</span>.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8">
            <p className="text-[#4A5568] text-base leading-relaxed font-light">
              Share a few details about your child and family. Our admissions team will
              respond within one working day with next steps, fees and availability.
            </p>
          </div>
        </div>

        {/* Steps */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-20">
          {ADMISSIONS_STEPS.map((s, i) => (
            <motion.div
              key={s.step}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="relative bg-[#F5F5F0] rounded-3xl p-7 border border-[#0F1B47]/6"
              data-testid={`admission-step-${s.step}`}
            >
              <div className="font-display text-5xl font-semibold text-[#648DE5] mb-4">
                {s.step}
              </div>
              <div className="font-display text-xl text-[#0F1B47] font-medium mb-2">
                {s.title}
              </div>
              <p className="text-sm text-[#4A5568] leading-relaxed font-light">{s.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Enquiry form */}
        <div className="grid lg:grid-cols-12 gap-8">
          {/* Left rail — section nav */}
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-32">
              <h3 className="font-display text-3xl text-[#0F1B47] font-medium mb-3">
                Admission Enquiry Form
              </h3>
              <p className="text-[#4A5568] text-sm leading-relaxed font-light mb-8">
                A short, premium enquiry. About 3 minutes. No obligation.
              </p>
              <ol className="space-y-2" data-testid="admissions-section-nav">
                {SECTIONS.map((sec, idx) => {
                  const Icon = sec.icon;
                  const active = activeSection === sec.id;
                  return (
                    <li key={sec.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setActiveSection(sec.id);
                          document
                            .getElementById(`section-${sec.id}`)
                            ?.scrollIntoView({ behavior: "smooth", block: "start" });
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-left transition-all border ${
                          active
                            ? "bg-[#0F1B47] text-[#FDFDF9] border-[#0F1B47]"
                            : "bg-[#F5F5F0] text-[#0F1B47] border-transparent hover:border-[#0F1B47]/15"
                        }`}
                        data-testid={`admissions-nav-${sec.id}`}
                      >
                        <div
                          className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                            active
                              ? "bg-[#C6D947] text-[#0F1B47]"
                              : "bg-[#FDFDF9] text-[#648DE5]"
                          }`}
                        >
                          <Icon size={16} />
                        </div>
                        <div>
                          <div className="text-[10px] font-bold tracking-[0.18em] uppercase opacity-80">
                            Step 0{idx + 1}
                          </div>
                          <div className="font-semibold text-sm">{sec.label}</div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ol>
              <div className="hidden lg:block mt-8 bg-[#F5F5F0] rounded-3xl p-6 border border-[#0F1B47]/6">
                <div className="text-xs font-bold tracking-[0.18em] uppercase text-[#648DE5] mb-2">
                  Already enrolled?
                </div>
                <p className="text-sm text-[#0F1B47] font-medium mb-1">
                  Returning families
                </p>
                <p className="text-xs text-[#4A5568] font-light">
                  Continue an enquiry or request sibling admissions — write to{" "}
                  <a
                    href="mailto:info@naaviearlyyears.edu.in"
                    className="text-[#0F1B47] underline underline-offset-2"
                  >
                    info@naaviearlyyears.edu.in
                  </a>
                  .
                </p>
              </div>
            </div>
          </aside>

          {/* Form */}
          <form
            id="admissions-form"
            onSubmit={handleSubmit(onSubmit, onInvalid)}
            className="lg:col-span-8 bg-[#F5F5F0] rounded-[32px] p-7 md:p-10 border border-[#0F1B47]/6"
            data-testid="admissions-form"
          >
            {submitted && (
              <div
                className="mb-8 flex items-start gap-3 bg-[#5BC4B8]/15 border border-[#5BC4B8]/40 rounded-2xl px-5 py-4 text-[#0F1B47]"
                data-testid="admissions-success"
              >
                <CheckCircle2 size={22} className="text-[#0F1B47] mt-0.5" />
                <div>
                  <div className="font-semibold text-sm">Enquiry received — thank you.</div>
                  <div className="text-sm font-light mt-1">
                    Our admissions team will write to you within one working day with next
                    steps, fees and availability.
                  </div>
                </div>
              </div>
            )}

            {/* SECTION: Program */}
            <FormSection
              id="section-program"
              eyebrow="Step 01"
              title="Program & academic year"
              description="Tell us when you'd like your child to begin and which Naavi program suits them best."
            >
              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Academic Year" required error={errors.academic_year?.message}>
                  <select
                    {...register("academic_year")}
                    className={inputCls}
                    data-testid="adm-academic-year"
                  >
                    <option value="">Choose academic year</option>
                    {ACADEMIC_YEARS.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Program / Class" required error={errors.program?.message}>
                  <select
                    {...register("program")}
                    className={inputCls}
                    data-testid="adm-program"
                  >
                    <option value="">Choose program</option>
                    {PROGRAMS.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} · {p.age}
                      </option>
                    ))}
                  </select>
                </Field>
                <div className="md:col-span-2">
                  <Field label="Preferred start" error={errors.preferred_start?.message}>
                    <select
                      {...register("preferred_start")}
                      className={inputCls}
                      data-testid="adm-preferred-start"
                    >
                      <option value="">When would you like to start?</option>
                      {PREFERRED_STARTS.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>
              </div>
            </FormSection>

            {/* SECTION: Student */}
            <FormSection
              id="section-student"
              eyebrow="Step 02"
              title="About your child"
              description="A few details about the little one joining us."
            >
              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Student's full name" required error={errors.student_name?.message}>
                  <input
                    {...register("student_name")}
                    className={inputCls}
                    placeholder="Kiaan Mehta"
                    data-testid="adm-student-name"
                  />
                </Field>
                <Field label="Date of birth" required error={errors.child_dob?.message}>
                  <input
                    type="date"
                    {...register("child_dob")}
                    className={inputCls}
                    data-testid="adm-child-dob"
                  />
                </Field>
                <Field label="Gender" required error={errors.gender?.message}>
                  <select
                    {...register("gender")}
                    className={inputCls}
                    data-testid="adm-gender"
                  >
                    <option value="">Select</option>
                    {GENDERS.map((g) => (
                      <option key={g.value} value={g.value}>
                        {g.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Nationality" error={errors.nationality?.message}>
                  <input
                    {...register("nationality")}
                    className={inputCls}
                    placeholder="Indian"
                    data-testid="adm-nationality"
                  />
                </Field>
                <div className="md:col-span-2">
                  <Field label="Languages spoken at home" error={errors.mother_tongue?.message}>
                    <input
                      {...register("mother_tongue")}
                      className={inputCls}
                      placeholder="English, Hindi, Tamil…"
                      data-testid="adm-mother-tongue"
                    />
                  </Field>
                </div>
                <div className="md:col-span-2">
                  <Field
                    label="Allergies, medical or sensory notes"
                    error={errors.medical_notes?.message}
                  >
                    <textarea
                      rows={3}
                      {...register("medical_notes")}
                      className={`${inputCls} resize-none`}
                      placeholder="Anything we should know to keep your child safe and comfortable?"
                      data-testid="adm-medical-notes"
                    />
                  </Field>
                </div>
              </div>
            </FormSection>

            {/* SECTION: Parents */}
            <FormSection
              id="section-parents"
              eyebrow="Step 03"
              title="Parents & guardians"
              description="So we can reach you with next steps."
            >
              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Father's name" required error={errors.father_name?.message}>
                  <input
                    {...register("father_name")}
                    className={inputCls}
                    placeholder="Rohan Mehta"
                    data-testid="adm-father-name"
                  />
                </Field>
                <Field label="Father's occupation" error={errors.father_occupation?.message}>
                  <input
                    {...register("father_occupation")}
                    className={inputCls}
                    placeholder="Architect"
                    data-testid="adm-father-occupation"
                  />
                </Field>
                <Field label="Mother's name" required error={errors.mother_name?.message}>
                  <input
                    {...register("mother_name")}
                    className={inputCls}
                    placeholder="Ananya Mehta"
                    data-testid="adm-mother-name"
                  />
                </Field>
                <Field label="Mother's occupation" error={errors.mother_occupation?.message}>
                  <input
                    {...register("mother_occupation")}
                    className={inputCls}
                    placeholder="Doctor"
                    data-testid="adm-mother-occupation"
                  />
                </Field>
                <Field label="Email" required error={errors.email?.message}>
                  <input
                    type="email"
                    {...register("email")}
                    className={inputCls}
                    placeholder="you@email.com"
                    data-testid="adm-email"
                  />
                </Field>
                <Field
                  label="WhatsApp / Mobile"
                  required
                  error={errors.mobile?.message || errors.country_code?.message}
                >
                  <div className="flex gap-2">
                    <select
                      {...register("country_code")}
                      className={`${inputCls} max-w-[110px]`}
                      data-testid="adm-country-code"
                    >
                      {COUNTRY_CODES.map((c) => (
                        <option key={c.code + c.country} value={c.code}>
                          {c.code} {c.country}
                        </option>
                      ))}
                    </select>
                    <input
                      {...register("mobile")}
                      className={inputCls}
                      placeholder="98 000 00000"
                      data-testid="adm-mobile"
                    />
                  </div>
                </Field>
                <Field label="Alternate phone" error={errors.alt_phone?.message}>
                  <input
                    {...register("alt_phone")}
                    className={inputCls}
                    placeholder="Optional"
                    data-testid="adm-alt-phone"
                  />
                </Field>
                <div className="md:col-span-2">
                  <Field label="Residential address" error={errors.address?.message}>
                    <input
                      {...register("address")}
                      className={inputCls}
                      placeholder="Area, City, PIN"
                      data-testid="adm-address"
                    />
                  </Field>
                </div>
              </div>
            </FormSection>

            {/* SECTION: Source */}
            <FormSection
              id="section-source"
              eyebrow="Step 04"
              title="How did you hear about Naavi?"
              description="It helps us understand what's working."
            >
              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Source of information" required error={errors.source?.message}>
                  <select
                    {...register("source")}
                    className={inputCls}
                    data-testid="adm-source"
                  >
                    <option value="">Select</option>
                    {SOURCES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </Field>
                {(sourceValue === "Referred by relatives or friends" ||
                  sourceValue === "Other") && (
                  <Field label="Referred by / details" error={errors.referred_by?.message}>
                    <input
                      {...register("referred_by")}
                      className={inputCls}
                      placeholder={
                        sourceValue === "Other" ? "Please specify" : "Family / friend's name"
                      }
                      data-testid="adm-referred-by"
                    />
                  </Field>
                )}
                <div className="md:col-span-2">
                  <Field label="Anything else we should know?" error={errors.message?.message}>
                    <textarea
                      rows={4}
                      {...register("message")}
                      className={`${inputCls} resize-none`}
                      placeholder="Sibling at Naavi, particular interests, questions for us…"
                      data-testid="adm-message"
                    />
                  </Field>
                </div>
              </div>
            </FormSection>

            <div className="mt-2 flex flex-col-reverse md:flex-row md:items-center md:justify-between gap-4 pt-2 border-t border-[#0F1B47]/8">
              <p className="text-xs text-[#718096] max-w-md leading-relaxed font-light">
                By submitting, you consent to Naavi Early Years contacting you about your
                enquiry. We never share your details with third parties.
              </p>
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center justify-center gap-2 bg-[#0F1B47] text-[#FDFDF9] rounded-full px-8 py-4 font-semibold text-[15px] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#1a2b6b] hover:shadow-xl disabled:opacity-60"
                data-testid="adm-submit"
              >
                {isSubmitting ? "Sending…" : "Submit enquiry"}
                <Send size={16} />
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

const inputCls =
  "w-full bg-[#FDFDF9] border border-[#0F1B47]/12 rounded-2xl px-5 py-3.5 text-[15px] text-[#0F1B47] placeholder:text-[#718096] font-medium focus:outline-none focus:border-[#648DE5] focus:ring-4 focus:ring-[#648DE5]/10 transition-all";

function Field({ label, children, error, required }) {
  return (
    <label className="block">
      <span className="text-xs font-bold tracking-[0.14em] uppercase text-[#0F1B47] mb-2 block">
        {label}
        {required && <span className="text-[#DC2626] ml-1">*</span>}
      </span>
      {children}
      {error && (
        <span className="text-xs text-red-600 mt-1.5 block font-medium">{error}</span>
      )}
    </label>
  );
}

function FormSection({ id, eyebrow, title, description, children }) {
  return (
    <section id={id} className="mb-10 scroll-mt-32">
      <div className="mb-6">
        <span className="text-[10px] font-bold tracking-[0.22em] uppercase text-[#648DE5]">
          {eyebrow}
        </span>
        <h4 className="font-display text-2xl text-[#0F1B47] font-medium mt-1">{title}</h4>
        <p className="text-sm text-[#4A5568] font-light mt-1.5">{description}</p>
      </div>
      {children}
    </section>
  );
}
